import React, { useEffect } from 'react';
import {
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Drawer,
  Typography,
  Divider,
  IconButton,
  Button
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ChevronLeft, ChevronRight, ExpandMore, ExpandLess } from '@mui/icons-material';
import clsx from 'clsx';
import resourcesIconPng from 'assets/images/icons/ResourcesWhite.png';
import { useResources, DRAWER_WIDTH } from 'state/providers/ResourcesProvider';
import { useFullScreen } from 'state/providers/FullScreenProvider';
import { createEvent } from 'api/telemetry';
import { remCalc } from 'util/style-util';
import { translate } from '../../translations/translator';

const t = key => translate('resources', key);
const ICON_SIZE = 40;

const ResourcesSlider = ({ workbook, currentPage, isZoom, handleSelectResource }) => {
  const [{ isOpen, resources }, { setIsOpen }] = useResources();
  const [{ isFullScreen }] = useFullScreen();
  const classes = useStyles()({ isFullScreen, workbook });
  const theme = useTheme();

  const categories = Object.keys(resources);

  const [expanded, setExpanded] = React.useState<string[]>(categories);

  useEffect(() => {
    setExpanded(categories);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length]);

  const handleChange = (panel: string) => (e: any, newExpanded: boolean) => {
    setExpanded(newExpanded ? [...expanded, panel] : [...expanded.filter(e => e !== panel)]);
  };

  const handleClick = resource => {
    handleSelectResource(resource);
    return createEvent('accessResource', {
      bookId: workbook.id,
      bookType: workbook.type,
      pageId: workbook.pageDetails?.[0].id,
      pageNumber: currentPage,
      resourceId: resource.id,
      resourceType: resource.type.type,
      pageLayout: isZoom ? 'singlePage' : 'doublePage'
    });
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={isOpen}
      PaperProps={{ elevation: 5 }}
      classes={{
        paper: classes.drawerPaper
      }}
    >
      <div
        className={classes.drawerHeader}
        style={{
          backgroundColor: isFullScreen ? 'white' : workbook.secondaryColor
        }}
      >
        <img src={resourcesIconPng} className={classes.iconImage} alt={t('title')} />

        <Typography variant="h4" style={{ padding: '5px 0 0 15px' }}>
          {t('title')}
        </Typography>
        <IconButton
          onClick={() => setIsOpen(false)}
          size="medium"
          style={{ color: 'black', padding: remCalc(12) }}
        >
          {theme.direction === 'rtl' ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </div>
      <Divider />
      {!categories.length && (
        <Typography className={classes.text} style={{ padding: '30px' }}>
          {t('noResources')}
        </Typography>
      )}
      <div>
        {categories.map(category => {
          const categoryResources = resources[category];
          return (
            <Accordion
              key={category}
              expanded={expanded.includes(category)}
              onChange={handleChange(category)}
              className={classes.accordion}
              square
            >
              <AccordionSummary aria-controls={`${category}-content`} className={classes.summary}>
                <div className={classes.categoryHeader}>
                  {category}
                  <IconButton
                    onClick={() => handleChange(category)}
                    size="medium"
                    style={{ color: workbook.textColor, padding: remCalc(12) }}
                  >
                    {expanded.includes(category) ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                {categoryResources.map(resource => (
                  <div key={resource.id} className={classes.detailsContentWrapper}>
                    <Button key={resource.id} onClick={() => handleClick(resource)}>
                      <div className={classes.contentWrapper}>
                        <div className={classes.topContent}>
                          <img
                            src={resource.type.icon}
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                            alt={resource.title}
                          />
                          <div
                            className={classes.text}
                            style={{ width: '80%', justifyContent: 'left' }}
                          >
                            {resource.title}
                          </div>
                        </div>
                        {resource.description && (
                          <div className={clsx(classes.text, classes.descriptionText)}>
                            {resource.description}
                          </div>
                        )}
                      </div>
                    </Button>
                  </div>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </div>
    </Drawer>
  );
};

export default React.memo(ResourcesSlider);

const useStyles = () =>
  makeStyles(() => ({
    drawer: {
      transition: '0.5s'
    },
    drawerPaper: ({ isFullScreen }: any) => ({
      height: `calc(100% - ${isFullScreen ? 150 : 75}px) !important`,
      width: DRAWER_WIDTH,
      top: `${isFullScreen ? remCalc(60) : 0} !important`,
      overflowY: 'auto',
      overflowX: 'hidden'
    }),
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: '0 8px',
      minHeight: remCalc(60),
      height: remCalc(60),
      transition: '0.5s'
    },
    categoryHeader: {
      lineHeight: '20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    contentWrapper: {
      display: 'flex',
      width: DRAWER_WIDTH - 15,
      flexDirection: 'column'
    },
    topContent: {
      display: 'flex',
      width: DRAWER_WIDTH - 15,
      flexDirection: 'row',
      alignItems: 'center'
    },
    iconImage: {
      height: remCalc(40)
    },
    text: {
      whiteSpace: 'normal',
      display: 'flex',
      justifyContent: 'center',
      textAlign: 'left',
      alignItems: 'center',
      padding: '2px 2px 2px 10px',
      fontSize: '16px',
      fontWeight: '500'
    },
    descriptionText: {
      paddingTop: remCalc(15),
      fontSize: remCalc(14),
      fontWeight: '300'
    },
    accordion: ({ workbook }: any) => ({
      backgroundColor: `${workbook.primaryColor} !important`,
      color: `${workbook.textColor} !important`,
      borderBottom: '1px solid white',
      '&.Mui-expanded': {
        margin: '0 !important',
        borderBottom: 'unset'
      }
    }),
    summary: {
      height: remCalc(55),
      padding: '5px 15px'
    },
    details: {
      display: 'flex',
      justifyContent: 'center',
      textAlign: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      padding: `0px !important`
    },
    detailsContentWrapper: {
      borderBottom: '1px solid black',
      height: 'max-content',
      padding: '15px 0px',
      backgroundColor: 'white',
      width: '100%',
      '& .MuiButton-text': {
        textTransform: 'unset',
        backgroundColor: 'white',
        color: 'black',
        width: DRAWER_WIDTH,
        height: '100%',
        lineHeight: remCalc(21),
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center',
        alignItems: 'center',
        border: 'none',
        borderRadius: '0px',
        '&:focus': {
          backgroundColor: 'white !important',
          color: 'black'
        },
        '&:hover': {
          color: 'black',
          backgroundColor: 'white !important',
          transform: 'scale(1.02, 1.02)'
        }
      }
    }
  }));
